<template>
    <div class="form-container">
        <h2 style="margin: 0 0 16px 0; color: var(--color-ds-primary);">Get Table Data</h2>
        <div class="form-section">
            <div class="form-group">
                <label>Model Name</label>
                <select v-model="localSelectedModel">
                    <option value="">Select Model</option>
                    <option v-for="model in props.models" :key="model.id" :value="model">
                        {{ model.name }}
                    </option>
                </select>
            </div>
            <div class="form-group">
                <label>Table Name</label>
                <select v-model="selectedTable">
                    <option value="">Select Table</option>
                    <option v-for="table in tables" :key="table.name" :value="table.name">
                        {{ table.name }}
                    </option>
                </select>
            </div>
            <button class="button button-full" @click="insertTableData">Get Data</button>
        </div>
    </div>
</template>

<script setup>
/// <reference types="office-js" />

import { defineProps, ref, computed } from 'vue';
import { gridInsertionInProgress } from '@/utils/eventBus';
import { getTableData } from '../services/optilogicService';
import { showMessage } from '../utils/messages';
import { useTableSelection } from '../composables/useTableSelection';
import { createCellName, handleEmptyCellForNaming, restoreEmptyCell } from '../utils/namedRangeUtils';

const props = defineProps({
    selectedModel: Object,
    models: []
});

// Local model selection
const localSelectedModel = ref(null);

// Create a computed prop object that matches the expected structure
const localPropsForComposable = computed(() => ({
    selectedModel: localSelectedModel.value,
    models: props.models
}));

// Use composable for table selection logic
const { selectedTable, tables } = useTableSelection(localPropsForComposable);

const insertTableData = async () => {
    gridInsertionInProgress.value = true;
    const table_data = await getTableData(localSelectedModel.value?.id, selectedTable.value);
    console.log("table data is", table_data);
    showMessage(`inserting data for ${selectedTable.value}`);

    try {
        await window.Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = context.workbook.getSelectedRange();
            const headers = Object.keys(table_data.data[0]);
            const rows = table_data.data.map((row) => Object.values(row));
            rows.unshift(headers);

            const rowCount = rows.length;
            const columnCount = rows[0].length;
            const targetRange = range.getResizedRange(rowCount - 1, columnCount - 1);

            targetRange.values = rows;
            targetRange.format.autofitColumns();
            targetRange.format.autofitRows();
            targetRange.load("address");

            const idIndex = headers.indexOf("id");
            const cellsToLoad = [];

            for (let i = 1; i < rowCount; i++) {
                const idValue = rows[i][idIndex];
                for (let j = 0; j < columnCount; j++) {
                    if (j !== idIndex) {
                        const columnName = headers[j];
                        const cell = targetRange.getCell(i, j);
                        const cellName = createCellName(localSelectedModel.value?.id, selectedTable.value, idValue, columnName);

                        cell.load("address, addressLocal, values");

                        const existingName = sheet.names.getItemOrNullObject(cellName);
                        existingName.load("isNullObject");

                        cellsToLoad.push({ cell, cellName, existingName });
                    }
                }
            }

            await context.sync();

            for (const { cell, cellName, existingName } of cellsToLoad) {
                try {
                    // Handle Excel API limitation: empty cells can't have named ranges
                    const wasEmpty = handleEmptyCellForNaming(cell);

                    if (!existingName.isNullObject) {
                        existingName.delete();
                    }

                    sheet.names.add(cellName, cell);

                    // Restore empty cell if needed
                    restoreEmptyCell(cell, wasEmpty);
                } catch (err) {
                    console.error(`Error processing cell ${cellName}:`, err);
                }
            }

            await context.sync();
            showMessage(`${selectedTable.value} inserted`);
        });
    } catch (error) {
        console.error("Error inserting data into Excel:", error);
        showMessage(`ERROR: inserting data for ${selectedTable.value}`);
    } finally {
        console.log('clearing gridInsertionInProgress');
        gridInsertionInProgress.value = false;
    }
};
</script>
